import { APP_TIME_ZONE } from '@saesak/shared';

const dateFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: APP_TIME_ZONE,
  month: 'long',
  day: 'numeric',
  weekday: 'short',
});

const timeFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: APP_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const fullFormatter = new Intl.DateTimeFormat('ko-KR', {
  timeZone: APP_TIME_ZONE,
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  return dateFormatter.format(new Date(value));
}

export function formatFullDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  return fullFormatter.format(new Date(value));
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  return timeFormatter.format(new Date(value));
}

/** "방금 전 · 3분 전 · 어제" 같은 상대 표기 */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '';
  const target = new Date(value).getTime();
  const diffMinutes = Math.round((Date.now() - target) / 60_000);

  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return '어제';
  if (diffDays < 7) return `${diffDays}일 전`;

  return formatDate(value);
}

/** 시간대에 맞는 인사 (§7.5 홈) */
export function greetingFor(date = new Date()): 'morning' | 'afternoon' | 'evening' {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: APP_TIME_ZONE,
      hour: '2-digit',
      hour12: false,
    }).format(date),
  );
  if (hour < 11) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * 전화번호 표시용 — 숫자만 저장된 값을 000-0000-0000 / 000-000-0000 꼴로.
 * 서울 유선(02)은 02-000(0)-0000. 자리수가 어긋나면 원문 그대로 둔다.
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.startsWith('02')) {
    if (digits.length === 10) return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    if (digits.length === 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return value;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return value;
}

/** 전화번호 입력용 — 입력하는 동안 대시를 자동으로 끼워 넣는다. onChange에서 부른다. */
export function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function todayISO(date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIME_ZONE }).format(date);
}

/** 조사 처리 — "하늘이가" / "도윤이가" 처럼 받침에 따라 달라지는 문장에 쓴다. */
export function withParticle(name: string, withBatchim: string, withoutBatchim: string): string {
  const last = name.trim().slice(-1);
  const code = last.charCodeAt(0);
  if (code < 0xac00 || code > 0xd7a3) return `${name}${withoutBatchim}`;
  const hasBatchim = (code - 0xac00) % 28 !== 0;
  return `${name}${hasBatchim ? withBatchim : withoutBatchim}`;
}

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * 그 주의 월요일 — 서버의 주간 식단 규칙과 같은 정의 (§5.7).
 * UTC 자정 기준으로만 계산해 로컬 타임존이 끼어들 여지를 없앤다.
 */
export function mondayOf(date: string): string {
  const utc = new Date(`${date}T00:00:00Z`);
  const weekday = (utc.getUTCDay() + 6) % 7; // 월=0
  return addDays(date, -weekday);
}

export function addDays(date: string, days: number): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}
